/**
 * `appsFromCapabilities` — the ONE reading of a capability ORDER as apps (APP-ENTITLEMENT-001 Phase 7): what a
 * company that holds `['pos', 'website', …]` is entitled to, derived from the catalog's own `capabilityKeys` rather
 * than a second table. Two readers share it: the backfill that turns every live `tenantCapabilities` row into
 * entitlement rows, and the console's tenant creation while it still takes a capability list (Phase 9 turns that
 * form into an anchor and apps).
 *
 * The rule: an app is named by a capability it grants. An anchor is named OUTRIGHT by a key only it grants (`pos`
 * → ParcelPuffin; `blog`, `owner-console`, `analytics`, `page-access` → PublishPuffin); a key the anchors share
 * (`website`, `hosting`) names PublishPuffin only when no anchor was named outright — beside ParcelPuffin it is a
 * ParcelPuffin feature. When two anchors are named outright, ParcelPuffin outranks. Every other key names exactly one
 * app. A key no app grants is reported, never dropped. The set may name no anchor (a company holding
 * only `business-development`): the caller decides, by ruling, what anchors it.
 *
 * @module @puffin-core/app-catalog/rules/capabilityMapping
 */

'use strict';

const { APP_CATALOG, WEB_ANCHOR_APP_KEY } = require('../catalog');
const { APP_KIND, APP_KIND_VALUES } = require('../vocabulary');

/**
 * @typedef {Object} AppsFromCapabilities
 * @property {?string} anchorAppKey - The anchor the keys name, or null when they name none.
 * @property {string[]} appKeys - Every non-anchor app named, in catalog order (free apps included when named).
 * @property {string[]} unmapped - Keys no catalog app grants.
 */

/**
 * @typedef {Object} CapabilityCatalogEntry - Exactly the facts this rule reads of one catalog entry.
 * @property {string} appKey
 * @property {string} kind - `APP_KIND`.
 * @property {string[]} capabilityKeys
 * @property {?string[]} attachesTo - The anchors an app attaches to; null for an anchor.
 */

/** The facts the rule reads of an entry: a frozen catalog carries these and nothing else. */
const ENTRY_FACTS = Object.freeze(['appKey', 'kind', 'capabilityKeys', 'attachesTo']);

const isKeyList = (value) =>
  Array.isArray(value) && value.every((key) => typeof key === 'string' && key.length > 0);

/**
 * A catalog a caller hands the rule, judged: `{ entries }`, a non-empty list in catalog order, each entry carrying
 * exactly `ENTRY_FACTS`, an anchor attaching to nothing and an app to at least one anchor.
 *
 * @param {unknown} catalog
 * @returns {ReadonlyArray<CapabilityCatalogEntry>}
 * @throws {TypeError} Naming the entry and the fact that is wrong.
 */
function judgedEntries(catalog) {
  if (
    !catalog ||
    typeof catalog !== 'object' ||
    !Array.isArray(catalog.entries) ||
    catalog.entries.length === 0
  ) {
    throw new TypeError('appsFromCapabilities: catalog must be { entries }, a non-empty list');
  }
  catalog.entries.forEach((entry, index) => {
    if (!entry || typeof entry.appKey !== 'string' || entry.appKey.length === 0) {
      throw new TypeError(`appsFromCapabilities: catalog entry ${index} has no appKey`);
    }
    const at = `appsFromCapabilities: catalog entry ${entry.appKey}`;
    const extra = Object.keys(entry).find((fact) => !ENTRY_FACTS.includes(fact));
    if (extra !== undefined) throw new TypeError(`${at}: ${extra} is not a fact this rule reads`);
    if (!APP_KIND_VALUES.includes(entry.kind)) {
      throw new TypeError(`${at}: kind must be one of ${APP_KIND_VALUES.join(', ')}`);
    }
    if (!isKeyList(entry.capabilityKeys)) {
      throw new TypeError(`${at}: capabilityKeys must be a list of keys`);
    }
    const attaches =
      entry.kind === APP_KIND.ANCHOR
        ? entry.attachesTo === null
        : isKeyList(entry.attachesTo) && entry.attachesTo.length > 0;
    if (!attaches) {
      throw new TypeError(
        `${at}: attachesTo must be ${entry.kind === APP_KIND.ANCHOR ? 'null for an anchor' : 'the anchors an app attaches to'}`,
      );
    }
  });
  return catalog.entries;
}

/**
 * @param {Iterable<string>} capabilities - A company's capability keys (its order).
 * @param {Object} [options]
 * @param {{entries: CapabilityCatalogEntry[]}} [options.catalog] - A FROZEN catalog to read instead of the live one: an
 *   APPLIED migration names the catalog it ran with (MAILBOX-RENT-PAYMENTS-001 U7, `migrationAppCatalogFrozen.contract`).
 *   Omitted, the rule reads `APP_CATALOG`, as every runtime caller should.
 * @returns {AppsFromCapabilities}
 * @throws {TypeError} When `capabilities` is not iterable, or `catalog` is malformed (by name).
 */
function appsFromCapabilities(capabilities, { catalog } = {}) {
  if (capabilities === null || typeof capabilities?.[Symbol.iterator] !== 'function') {
    throw new TypeError('appsFromCapabilities: capabilities must be iterable');
  }
  const entries = catalog === undefined ? APP_CATALOG : judgedEntries(catalog);
  const held = new Set(capabilities);
  const anchors = entries.filter((entry) => entry.kind === APP_KIND.ANCHOR);
  const apps = entries.filter((entry) => entry.kind !== APP_KIND.ANCHOR);
  // A key exactly ONE anchor grants, and no app, names it outright (`pos` → ParcelPuffin, `field-service` →
  // ProPuffin). A key the anchors share (the hosted website's set: `website`, `hosting`, `analytics`,
  // `owner-console`, `page-access`), or one an anchor AND an app grant (`blog`: PublishPuffin's site includes it,
  // the Auto-blog app sells it beside another anchor), names PublishPuffin only when no anchor was named outright —
  // beside ParcelPuffin it is a feature of ParcelPuffin's, or the Auto-blog app's.
  const anchorsGranting = (key) => anchors.filter((entry) => entry.capabilityKeys.includes(key));
  const appsGranting = (key) => apps.filter((entry) => entry.capabilityKeys.includes(key));
  const namedOutright = anchors.filter((entry) =>
    entry.capabilityKeys.some(
      (key) => held.has(key) && anchorsGranting(key).length === 1 && appsGranting(key).length === 0,
    ),
  );
  const namedByShared = anchors.filter((entry) =>
    entry.capabilityKeys.some(
      (key) => held.has(key) && (anchorsGranting(key).length > 1 || appsGranting(key).length > 0),
    ),
  );
  const anchorCandidates =
    namedOutright.length > 0
      ? namedOutright
      : namedByShared.filter((entry) => entry.appKey === WEB_ANCHOR_APP_KEY);
  // The first candidate in the catalog's own anchor order (for the live catalog, `ANCHOR_APP_KEYS`).
  const anchor = anchors.find((entry) => anchorCandidates.includes(entry)) || null;
  // An app names itself by a held key only where it can ATTACH: beside PublishPuffin a held `blog` is the anchor's
  // own feature, never the Auto-blog app.
  const appKeys = apps
    .filter(
      (entry) =>
        entry.capabilityKeys.some((key) => held.has(key)) &&
        (anchor === null || entry.attachesTo.includes(anchor.appKey)),
    )
    .map((entry) => entry.appKey);
  const granted = new Set(entries.flatMap((entry) => entry.capabilityKeys));
  const unmapped = [...held].filter((key) => !granted.has(key));
  return { anchorAppKey: anchor === null ? null : anchor.appKey, appKeys, unmapped };
}

module.exports = { appsFromCapabilities };
